import { useEffect, useState } from "react";

import {
  Modal,
  Paper,
  TextField,
  Grid,
  Button,
  Typography,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Alert,
  Divider,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  Autocomplete,
} from "@mui/material";

import { Location } from "types";
import CustomNumberInput from "components/CustomNumberInput";
import { LocationTypes } from "constants/index";
import { useSession } from "providers/SessionProvider";
import { getUserFullName } from "helpers";

type Props = {
  open: boolean;
  handleOpen: () => void;
  value: Location;
  onEdit: (location: Location) => Promise<Location | null>;
};

type SubAction = "" | "fractionate" | "consolidate";

export default function EditLocationModal({
  open = true,
  handleOpen,
  value,
  onEdit,
}: Props) {
  const { users, stores } = useSession();
  const [data, setData] = useState<Location>(value);
  const [subAction, setSubAction] = useState<SubAction>("");
  const [parts, setParts] = useState<number>(2);
  const [saving, setSaving] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [blockedSubs, setBlockedSubs] = useState<
    Array<{ id: number; code: string; total_stock?: number; total_reserved?: number }>
  >([]);

  useEffect(() => {
    if (open) {
      setData(value);
      setSubAction("");
      setParts(2);
      setApiError(null);
      setBlockedSubs([]);
      setSaving(false);
    }
  }, [open, value]);

  const handleChange = (name: string, value: any) => {
    setData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    if (!stores.some((store) => store.id === data.store_id)) {
      setApiError("Selecciona un almacén válido.");
      return;
    }
    setSaving(true);
    setApiError(null);
    setBlockedSubs([]);

    try {
      const payload: any = { ...data };
      if (subAction === "fractionate") {
        payload.sub_locations_action = "fractionate";
        payload.fractionate_parts = parts;
      } else if (subAction === "consolidate") {
        payload.sub_locations_action = "consolidate";
        payload.fractionate_parts = null;
      } else {
        payload.sub_locations_action = null;
        payload.fractionate_parts = null;
      }

      const result = await onEdit(payload);

      if (result) {
        handleOpen();
      }
    } catch (err: any) {
      const status = err?.status ?? err?.response?.status;
      const message =
        err?.payload?.message ??
        err?.response?.data?.message ??
        err?.message ??
        "Error inesperado";

      if (status === 409) {
        const blocked =
          err?.payload?.blocked_sub_locations ??
          err?.response?.data?.blocked_sub_locations ??
          [];
        setBlockedSubs(blocked);
      }

      setApiError(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={handleOpen}>
      <Paper
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "90%", md: "30rem" },
          boxSizing: "border-box",
          maxHeight: "90vh",
          overflowY: "auto",
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 4,
        }}
      >
        <Grid container spacing={2}>
          <Typography variant="h5" gutterBottom>
            Editar ubicación
          </Typography>
        </Grid>

        {apiError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {apiError}
            {blockedSubs.length > 0 && (
              <div style={{ marginTop: 8 }}>
                Sub-ubicaciones con mercadería:
                <ul style={{ margin: "6px 0 0 18px" }}>
                  {blockedSubs.map((b) => (
                    <li key={b.id}>
                      {b.code}
                      {typeof b.total_stock === "number" || typeof b.total_reserved === "number"
                        ? ` (stock: ${b.total_stock ?? 0}, reservado: ${b.total_reserved ?? 0})`
                        : ""}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </Alert>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              required
              id="outlined-required"
              label="Código"
              fullWidth
              value={data.code}
              onChange={(e) => handleChange("code", e.target.value)}
            />
          </Grid>

          <Grid item xs={12}>
            <Autocomplete
              options={stores}
              value={stores.find((store) => store.id === data.store_id) ?? null}
              isOptionEqualToValue={(option, selected) => option.id === selected.id}
              getOptionLabel={(option) => option.description}
              disabled={saving}
              onChange={(_, store) => {
                setData((prev) => ({
                  ...prev,
                  store_id: store?.id ?? 0,
                  ...(store ? { store } : {}),
                }));
              }}
              renderInput={(params) => (
                <TextField {...params} required label="Almacén" />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth required margin="none">
              <InputLabel id="location_type-edit-label">Tipo</InputLabel>
              <Select
                labelId="location_type-edit-label"
                label={"Tipo"}
                value={data.location_type}
                onChange={(e) => handleChange("location_type", e.target.value)}
                fullWidth
                required
              >
                <MenuItem key={LocationTypes.PISO} value={LocationTypes.PISO}>
                  {LocationTypes.PISO}
                </MenuItem>
                <MenuItem key={LocationTypes.RACK} value={LocationTypes.RACK}>
                  {LocationTypes.RACK}
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <Grid container justifyContent={"space-between"}>
              <Grid item xs={3.5}>
                <CustomNumberInput
                  required
                  label="Ancho"
                  value={data.width}
                  onChange={(e) => handleChange("width", e.target.value)}
                />
              </Grid>
              <Grid item xs={3.5}>
                <CustomNumberInput
                  required
                  label="Alto"
                  value={data.height}
                  onChange={(e) => handleChange("height", e.target.value)}
                />
              </Grid>
              <Grid item xs={3.5}>
                <CustomNumberInput
                  required
                  label="Largo"
                  value={data.long}
                  onChange={(e) => handleChange("long", e.target.value)}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={Boolean(data.is_hidden)}
                  onChange={(e) =>
                    handleChange("is_hidden", e.target.checked)
                  }
                />
              }
              label="Ocultar ubicación a clientes"
            />
          </Grid>
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle1" fontWeight={600}>
              Sub-ubicaciones
            </Typography>

            <RadioGroup
              value={subAction}
              onChange={(e) => setSubAction(e.target.value as SubAction)}
            >
              <FormControlLabel value="" control={<Radio />} label="Sin acción" />
              <FormControlLabel
                value="fractionate"
                control={<Radio />}
                label="Fraccionar en partes"
              />
              <FormControlLabel
                value="consolidate"
                control={<Radio />}
                label="Consolidar"
              />
            </RadioGroup>

            {subAction === "fractionate" && (
              <Grid container spacing={1} alignItems="center">
                <Grid item xs={12}>
                  <CustomNumberInput
                    required
                    label="Cantidad de partes"
                    value={parts}
                    onChange={(e) => setParts(Number(e.target.value))}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Alert severity="info">
                    Se crearán sub-ubicaciones con formato: <b>{data.code}-00</b>, <b>{data.code}-01</b>, etc.
                    <br />
                    Ancho/Alto se dividirán por la cantidad de partes.
                  </Alert>
                </Grid>
              </Grid>
            )}

            {subAction === "consolidate" && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                La consolidación solo será posible si ninguna sub-ubicación tiene mercadería.
              </Alert>
            )}
          </Grid>

          <Grid item xs={12}>
            <TextField
              disabled
              fullWidth
              label="Creado por"
              value={
                data.created_by
                  ? getUserFullName(data.created_by, users)
                  : "SISTEMA"
              }
            />
          </Grid>

          {/* acciones */}
          <Grid item xs={12} display="flex" justifyContent="end" gap={1}>
            <Button variant="outlined" onClick={handleOpen} disabled={saving}>
              Cancelar
            </Button>

            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving}
            >
              Guardar
            </Button>
          </Grid>
        </Grid>
      </Paper>
    </Modal>
  );
}
